import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
// Helpers
import { renderedCheckForBooleans } from 'helpers/utils';
import {
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGN_TYPE_FILTER_OPTIONS,
  getCampaignTypeLabel,
  mapCampaignStatusToLabel,
} from 'helpers/campaign';
// Api
import { GET_CAMPAIGNS_V2 } from 'api/campaignV2/queries';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2Variables,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search_BookMeSearchCriteria,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignsOrderBy,
  SortDirection,
  CampaignType,
} from 'api/graphql-global-types';
// UI
import Table, { SortedInfo, TableFilter } from 'ui/Table';
// Components
import CampaignsActions from '../CampaignsActions/CampaignsActions';

const isBookMeSearchCriteria = (
  search: GetCampaignsV2_getCampaigns_entities['search']
): search is GetCampaignsV2_getCampaigns_entities_search_BookMeSearchCriteria => {
  return search?.__typename === 'BookMeSearchCriteria';
};

const PAGE_SIZE = 10;

const CampaignsTable = (): JSX.Element => {
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [sort, setSortInfo] = useState<SortedInfo<CampaignsOrderBy>>({
    order: SortDirection.DESC,
    key: CampaignsOrderBy.createdAt,
  });

  const getCampaignsInput: any = () => {
    const input: any = {
      direction: sort.order,
      orderBy: sort.key,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      if (key === 'type') {
        return (input['types'] = [value]);
      }
      if (key === 'statuses') {
        return (input['statuses'] = Array.isArray(value) ? value : [value]);
      }
      input[key] = value;
    });

    return { input };
  };

  const { data, loading, refetch } = useQuery<
    GetCampaignsV2,
    GetCampaignsV2Variables
  >(GET_CAMPAIGNS_V2, {
    variables: {
      ...getCampaignsInput(),
    },
    fetchPolicy: 'cache-and-network',
  });

  const columns = [
    {
      title: () => (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          Campaign Version
          <Tooltip title="Social campaigns can be v1, v2, v3 and book me and others will all be Version 1">
            <InfoCircleFilled
              style={{ fontSize: '12px', color: '#1890ff', cursor: 'help' }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'v',
      width: 100,
    },
    {
      title: 'Campaign ID',
      dataIndex: 'id',
      width: 150,
    },
    {
      title: 'External Campaign Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      withSearch: true,
    },
    {
      title: 'Internal Campaign Name',
      dataIndex: 'internalName',
      key: 'internalName',
      width: 200,
      withSearch: true,
    },
    {
      title: 'Brand name',
      dataIndex: 'brandName',
      key: 'brandName',
      width: 150,
      withSearch: true,
    },
    {
      title: () => (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          Talent Name
          <Tooltip title="Talent Name is only available for BookMe campaigns">
            <InfoCircleFilled
              style={{ fontSize: '12px', color: '#1890ff', cursor: 'help' }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'talentName',
      key: 'talentName',
      width: 150,
      withSearch: true,
    },
    {
      title: 'Price Per Talent',
      dataIndex: 'pricePerTalent',
      width: 200,
      align: 'right' as const,
      render: (_: string, record: GetCampaignsV2_getCampaigns_entities) => {
        const price = record.flatFeePerStore;

        return price != null ? (
          <span>${price.toFixed(2)}</span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: 'Maximum Budget',
      dataIndex: 'estimatedBudget',
      width: 150,
      align: 'right' as const,
      render: (value: number) => {
        return value != null ? (
          <span>${value.toFixed(2)}</span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: 'Total Budget',
      dataIndex: 'budget',
      width: 150,
      align: 'right' as const,
      render: (value: number) => {
        return value != null ? (
          <span>${value.toFixed(2)}</span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: 'Margin',
      dataIndex: 'platformMarginPercent',
      width: 100,
      align: 'right' as const,
      render: (value: number | null) => {
        return value != null ? <span>{value}%</span> : <span>N/A</span>;
      },
    },
    {
      title: 'Campaign Owner - Email',
      dataIndex: 'agencyEmail',
      key: 'agencyEmail',
      width: 200,
      withSearch: true,
      render: function AgencyEmail(
        _: string,
        record: GetCampaignsV2_getCampaigns_entities
      ) {
        return <span>{record.agency?.email || 'N/A'}</span>;
      },
    },
    {
      title: '# of Collaborators',
      dataIndex: 'collaborators',
      align: 'center' as const,
      width: 150,
      render: (
        collaborators: GetCampaignsV2_getCampaigns_entities['collaborators']
      ) => {
        return <span>{collaborators?.length || 0}</span>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      align: 'center' as const,
      width: 100,
      withRadioFilters: true,
      render: (type: CampaignType) => {
        return getCampaignTypeLabel(type);
      },
      filters: CAMPAIGN_TYPE_FILTER_OPTIONS,
    },
    {
      title: 'Due date',
      dataIndex: 'dueDate',
      align: 'center' as const,
      width: 100,
      render: function DisplayDate(
        dueData: string,
        item: GetCampaignsV2_getCampaigns_entities
      ) {
        const dueDate = item.dueDate
          ? moment(item.dueDate).local().format('MM/DD/YYYY, h:mm a')
          : 'N/A';

        return <span>{dueDate}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'statuses',
      width: 150,
      withCheckboxFilters: true,
      filters: CAMPAIGN_STATUS_OPTIONS,
      render: mapCampaignStatusToLabel,
    },
    {
      title: 'Physical product',
      dataIndex: 'physicalProduct',
      align: 'center' as const,
      width: 100,
      render: renderedCheckForBooleans,
    },

    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 80,
      render: function ActionsWrap(
        id: string,
        record: GetCampaignsV2_getCampaigns_entities
      ) {
        return <CampaignsActions data={record} refetchCampaigns={refetch} />;
      },
    },
  ];

  const updatedData: GetCampaignsV2_getCampaigns_entities[] =
    data?.getCampaigns.entities.map((campaign) => {
      let talentName = null;
      if (
        campaign.type === CampaignType.BookMe &&
        isBookMeSearchCriteria(campaign.search)
      ) {
        const talent = campaign.search.talent;
        if (talent) {
          talentName =
            `${talent.firstName} ${talent.lastName}` ||
            talent.storeDetails?.storeName;
        }
      }

      const baseCampaign = {
        ...campaign,
        agencyEmail: campaign.agency?.email || null,
        talentName,
      };

      if (
        campaign.activities?.[0]?.__typename === 'SponsoredAppearanceActivity'
      ) {
        if (
          campaign.activities?.[0]?.target?.__typename ===
          'SponsoredAppearanceGuest'
        ) {
          return {
            ...baseCampaign,
            brandName: campaign.activities[0].target?.guestName || null,
          };
        } else if (
          campaign.activities?.[0]?.target?.__typename ===
          'SponsoredAppearanceProduct'
        ) {
          return {
            ...baseCampaign,
            brandName: campaign.activities[0].target?.brandName || null,
            physicalProduct: campaign.activities[0]?.target?.physicalProduct,
          };
        } else {
          return baseCampaign;
        }
      } else if (
        campaign.activities?.[0]?.__typename === 'SocialBoostActivity'
      ) {
        return {
          ...baseCampaign,
          brandName: campaign.activities[0].brandName || null,
        };
      } else if (
        campaign.activities?.[0]?.__typename === 'SocialShareActivity'
      ) {
        return {
          ...baseCampaign,
          brandName: campaign.activities[0].brandName || null,
        };
      } else if (campaign.activities?.[0]?.__typename === 'SocialActivity') {
        return {
          ...baseCampaign,
          physicalProduct: campaign.activities[0]?.physicalProduct,
        };
      } else if (campaign.activities?.[0]?.__typename === 'UgcActivity') {
        const ugcActivity = campaign.activities[0] as any;
        return {
          ...baseCampaign,
          brandName: ugcActivity?.brandName || null,
          physicalProduct: ugcActivity?.physicalProduct,
        };
      } else if (
        campaign.activities?.[0]?.__typename === 'CustomBriefActivity'
      ) {
        const customBriefActivity = campaign.activities[0] as any;
        return {
          ...baseCampaign,
          brandName: customBriefActivity?.brandName || null,
          physicalProduct: customBriefActivity?.physicalProduct,
        };
      } else {
        return baseCampaign;
      }
    }) || [];

  return (
    <Table<GetCampaignsV2_getCampaigns_entities, CampaignsOrderBy>
      columns={columns}
      data={updatedData}
      scroll={{ x: 300 }}
      loading={loading}
      total={data?.getCampaigns.total}
      setPageSize={setPageSize}
      setCurrentPage={setCurrentPage}
      searchFilters={searchFilters}
      setSearchFilters={setSearchFilters}
      sortInfo={sort}
      setSortInfo={setSortInfo}
    />
  );
};

export default CampaignsTable;
